import { debounce, isBoolean } from 'lodash-es';
import jwt_decode from 'jwt-decode';
import moment from 'moment';
import { makeSOPClient, makeSOPClientProtocol, TOKEN_DEADLINE } from '@barracuda/sop-api-client';
const debug = process.env.REACT_APP_MODE === 'dev';
const VALIDATE_TOKEN_SECONDS = 10;
const ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS = 30;
const ACTIVITY_TIMEOUT_MINUTES = 10;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
const REFRESH_TOKEN_MAX_RETRIES = 5;
const REFRESH_TOKEN_RETRY_DELAY_MS = 5000;
export var TokenRefreshErrorLevel;
(function (TokenRefreshErrorLevel) {
    TokenRefreshErrorLevel["WARNING"] = "warning";
    TokenRefreshErrorLevel["FATAL"] = "fatal";
})(TokenRefreshErrorLevel || (TokenRefreshErrorLevel = {}));
const tokenRefreshErrorListeners = new Set();
let refreshInProgress = false;
let lastActiveAt = Date.now();
export const markUserPresence = async (e) => {
    lastActiveAt = Date.now();
    if (debug) {
        if (e?.target instanceof HTMLElement) {
            console.log(`Auth.markUserPresence(${e.type})`, e.target.nodeName);
        }
        else {
            console.log(`Auth.markUserPresence(${e?.type})`);
        }
    }
};
const markUserPresenceDebounced = debounce(markUserPresence, ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS * 1000, { leading: true, maxWait: ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS * 1000 });
ACTIVITY_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, markUserPresenceDebounced, {
        passive: true
    });
});
export const sopClient = makeSOPClient(makeSOPClientProtocol({
    baseUrl: process.env.REACT_APP_PESC_URL
}));
const dispatchLocalStorageEvent = (key, oldValue, newValue) => {
    window.dispatchEvent(new StorageEvent('storage', {
        storageArea: window.localStorage,
        key,
        oldValue,
        newValue
    }));
};
// at first run it is not yet stored in sop client
export const getSopToken = () => {
    return sopClient.session.getToken() || localStorage.getItem('token') || undefined;
};
export const removeSopToken = () => {
    setSopToken();
};
export const loadExistingSopToken = () => {
    setSopToken(getSopToken());
};
export const setSopToken = (token) => {
    if (isTokenExpired(token)) {
        token = undefined;
    }
    if (debug) {
        console.log(`Auth.setSopToken(${token})`);
    }
    const oldToken = sopClient.session.getToken();
    if (oldToken === token) {
        if (!token) {
            sopClient.session.deleteToken();
            localStorage.removeItem('token');
        }
        return;
    }
    if (token) {
        sopClient.session.setToken(token);
        localStorage.setItem('token', token);
    }
    else {
        sopClient.session.deleteToken();
        localStorage.removeItem('token');
    }
    dispatchLocalStorageEvent('token', oldToken, token || undefined);
};
export const getOAuthUrl = ({ verificationLicenseId, ageConsentConfirmed, termsAndPrivacyAccepted, receiveMarketing, desktop }) => {
    const baseUrl = process.env.REACT_APP_PESC_URL;
    const redirectUrl = `${baseUrl}/api/authentication/oauth2/redirect/google`;
    const url = new URL(redirectUrl);
    const searchParams = new URLSearchParams();
    searchParams.append('url', `${baseUrl}/${desktop ? 'desktop-callback' : 'callback'}`);
    const searchParamsFlags = {
        ageConsentConfirmed,
        termsAndPrivacyAccepted,
        receiveMarketing
    };
    Object.entries(searchParamsFlags).forEach(([key, value]) => {
        if (isBoolean(value)) {
            searchParams.append(key, value.toString());
        }
    });
    searchParams.append('applicationCode', 'ODYSSEY');
    if (verificationLicenseId) {
        searchParams.append('verificationLicenseId', verificationLicenseId.toString());
    }
    url.search = searchParams.toString();
    return url.toString();
};
export const signUp = async ({ email, password, captchaResult, dateOfBirth, countryOfResidenceId, consents }) => {
    const response = await sopClient.signUp({
        email,
        password,
        captchaResult,
        consents,
        dateOfBirth,
        countryOfResidenceId
    });
    if (response.status === 409) {
        return Promise.reject(response);
    }
    return response;
};
export const completeSSOSignUp = async ({ stateToken, captchaResult, dateOfBirth, countryOfResidenceId, consents }) => {
    const response = await sopClient.completeSSOSignUp({
        stateToken,
        captchaResult,
        dateOfBirth,
        countryOfResidenceId,
        consents
    });
    if (response.data?.token) {
        setSopToken(response.data.token);
    }
    return response;
};
export const signIn = async (email, password, ignoreSavingToken, captchaResult) => {
    markUserPresence();
    const response = await sopClient.signIn(email, password, captchaResult);
    if (response.data.token && !ignoreSavingToken) {
        setSopToken(response.data.token);
    }
    return response;
};
export const logout = async (loggedBefore) => {
    await addLoggedBefore(loggedBefore);
    await sopClient.logout();
    removeSopToken();
    localStorage.removeItem('recentOrder');
};
export const addLoggedBefore = async (loggedBefore) => {
    const payload = {
        loggedBefore: 'true'
    };
    if (!loggedBefore) {
        await sopClient.addMetadataToProfile(payload);
    }
};
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const notifyTokenRefreshError = (event) => {
    tokenRefreshErrorListeners.forEach((cb) => cb(event));
};
const refreshToken = async () => {
    for (let attempt = 1; attempt <= REFRESH_TOKEN_MAX_RETRIES; attempt++) {
        try {
            const response = await sopClient.refreshToken();
            const token = response?.data?.token;
            setSopToken(token);
            return;
        }
        catch (error) {
            console.log(`Token refresh attempt ${attempt}/${REFRESH_TOKEN_MAX_RETRIES} failed`, error);
            if (attempt < REFRESH_TOKEN_MAX_RETRIES) {
                notifyTokenRefreshError({
                    level: TokenRefreshErrorLevel.WARNING,
                    attempt,
                    maxRetries: REFRESH_TOKEN_MAX_RETRIES,
                    error
                });
                await delay(REFRESH_TOKEN_RETRY_DELAY_MS);
            }
            else {
                notifyTokenRefreshError({
                    level: TokenRefreshErrorLevel.FATAL,
                    attempt,
                    maxRetries: REFRESH_TOKEN_MAX_RETRIES,
                    error
                });
                removeSopToken();
            }
        }
    }
};
export const isTokenCloseToExpired = (token) => {
    const timeLeft = getTokenExpirationTime(token);
    return timeLeft < TOKEN_DEADLINE;
};
export const isTokenExpired = (token) => {
    if (token) {
        try {
            return getTokenExpirationTime(token) <= 0;
        }
        catch (e) {
            console.log('Token is invalid', e);
        }
    }
    return true;
};
export const getTokenExpirationTime = (token) => {
    if (token) {
        try {
            const decodedToken = jwt_decode(token);
            return moment(moment(decodedToken?.exp * 1000)).diff(Date.now(), 'seconds');
        }
        catch (e) {
            console.log('Token is invalid', e);
        }
    }
    return 0;
};
let sopTokenCheckTimeout;
const checkSopToken = () => {
    clearTimeout(sopTokenCheckTimeout);
    sopTokenCheckTimeout = setTimeout(() => checkSopToken(), VALIDATE_TOKEN_SECONDS * 1000);
    const sopToken = getSopToken();
    if (sopToken) {
        if (isTokenExpired(sopToken)) {
            removeSopToken();
            return;
        }
        if (!refreshInProgress && isTokenCloseToExpired(sopToken)) {
            const now = Date.now();
            const howManyMinutesAgoWasActive = (now - lastActiveAt) / (1000 * 60);
            if (howManyMinutesAgoWasActive < ACTIVITY_TIMEOUT_MINUTES) {
                refreshInProgress = true;
                refreshToken().finally(() => (refreshInProgress = false));
            }
        }
    }
};
export const addTokenRefreshErrorListener = (callback) => {
    tokenRefreshErrorListeners.add(callback);
    return () => {
        tokenRefreshErrorListeners.delete(callback);
    };
};
export const addTokenUpdatedListener = (callback) => {
    let listener = (e) => {
        if (e.storageArea === window.localStorage && e.key === 'token') {
            if (e.oldValue !== e.newValue) {
                const token = e.newValue ? e.newValue : undefined;
                if (debug) {
                    console.log('Auth.tokenUpdatedEvent', token, e);
                }
                sopClient.session.setToken(token);
                callback(token);
            }
        }
    };
    window.addEventListener('storage', listener);
    checkSopToken();
    return () => {
        window.removeEventListener('storage', listener);
        clearTimeout(sopTokenCheckTimeout);
    };
};
//# sourceMappingURL=Auth.js.map